import PropTypes from "prop-types";
import { useCallback, useContext, useEffect } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchFicheProduit, fetchFicheProduitUri } from "app/pages/Listing/productActionCreators";
import { toggleAsideMenu } from "app/pages/Header/menuActionCreators";
import { clearBooking, updateBooking } from "app/pages/Booking/bookingActionCreators";
import { closeSlideshow, openSlideshow } from "app/pages/.shared/Slideshow/SlideShowActionCreators";
import { ficheProduitPropTypes } from "app/utils/propTypes";
import { FLAGSHIP_SCREENVIEW_KPIS, OFFER_CONTRACT_TYPES, OFFER_TYPES } from "app/constants";
import {
	getDesktopSlideShowPhotos,
	getSpecialOffer,
	getVideos,
} from "app/pages/FicheProduit/ficheProduitSelector";
import {
	getAllAvailableDurations,
	getAllNegociated,
	getAvailablePrepackageOffers,
} from "app/pages/PrePackage/containers/searchEngineSelectors";
import { sendTagOnProductPageLoad } from "app/utils/analytics";
import { resetPrint, startPrint } from "app/reducers/printActionCreators";
import { saveNavLog } from "app/pages/Listing/NavLogs/navLogActionCreators";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import FicheProduit from "app/pages/FicheProduit/components/FicheProduit";
import { shouldFetchNavlogs } from "app/pages/Listing/NavLogs/navLogsSelector";
import { isWhiteLabel } from "app/reducers/brandSelector";
import { updateCurrentMerchCode } from "app/pages/Merchandising/merchandisingActionCreators";
import { parseQueries } from "app/utils/routerUtils";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { getShowPfsMessage } from "app/reducers/partnerSelector";
import AppGlobalsContext from "app/AppGlobalsContext";
import { extractProductUriFromEndpoint } from "app/utils/urlDataExtractor";
import usePrintMode from "app/reducers/usePrintMode";
import { HitType, useFlagship } from "@flagship.io/react-sdk";

const FicheProduitContainer = props => {
	const {
		ficheProduit,
		fetchFicheProduit,
		fetchFicheProduitUri,
		updateBooking,
		clearBooking,
		saveNavLog,
		shouldFetchNavlogs,
		closeSlideshow,
		resetPrint,
		updateCurrentMerchCode,
		resolution,
		startPrint,
		showTransportOfferOnly,
	} = props;

	const { shop, productsVisibility } = useContext(AppGlobalsContext);

	const { search } = useLocation();
	const { fromMerch } = parseQueries(search);
	const match = useRouteMatch();
	const history = useHistory();
	const isProductPageWithId = match.path === "/product/id/:productId";
	const isDesktop = resolution === RESOLUTION.LARGE || resolution === RESOLUTION.XLARGE;

	const isPrintMode = usePrintMode();

	const { hit: fsHit } = useFlagship();

	useEffect(() => {
		fsHit.sendMultiple([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: FLAGSHIP_SCREENVIEW_KPIS.ACCESS_TO_PRODUCT_PAGE,
			},
		]);
	}, []);

	useEffect(() => {
		if (isPrintMode) {
			startPrint();
		}
	}, [isPrintMode]);

	const fetchFicheProduitData = useCallback(() => {
		if (isProductPageWithId) {
			fetchFicheProduitUri({
				productId: match.params.productId,
				shop,
				productsVisibility,
			}).then(res => {
				if (res.status === 200) {
					const productUri = extractProductUriFromEndpoint(res.data);
					history.push(`/${productUri}`);
				}
			});
		} else {
			fetchFicheProduit({
				productUri: match.params.productUri,
				productId: match.params.productId,
			}).then(res => {
				if (res.status === 200) {
					const offers = res?.data?.offers;

					if (offers.length === 1 || showTransportOfferOnly) {
						const offer = offers[0];
						const offerType = offer.pricing.type;
						let offerToSave = {};
						let configToSave = undefined;

						if (offerType === OFFER_TYPES.ACCOMODATION_ONLY) {
							const { type, label, contractType, uri, config } = offer.pricing;
							offerToSave = {
								type,
								label,
								contractType,
								uri,
								negotiated: offer.negotiated,
							};
							configToSave = config;
						} else if (offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION) {
							const { type, label, contractType, config } = offer.pricing;

							// Dans le cas d'un rental, l'offre AO doit toujours être sélectionné par défaut sinon
							// les durées ne s'affichent pas.
							// On fait un updateBooking ici à cause du FETCH_PRODUCTS_SUCCESS qui clear les selections du user
							if (contractType === OFFER_CONTRACT_TYPES.RENTAL) {
								offerToSave = {
									type: OFFER_TYPES.ACCOMODATION_ONLY,
									contractType: OFFER_CONTRACT_TYPES.RENTAL,
								};
							} else {
								offerToSave = {
									type,
									label,
									contractType,
									negotiated: offer.negotiated,
								};
								configToSave = config;
							}
						}

						updateBooking({
							config: configToSave,
							offer: offerToSave,
						});
					}

					if (shouldFetchNavlogs) {
						const product = res.data;
						saveNavLog({ product });
					}
					sendTagOnProductPageLoad();
				}
			});
		}
	}, [match.params.productUri, match.params.productId, shouldFetchNavlogs]);

	useEffect(() => {
		const productUriFromUrl = match.params.productUri;
		const productIdFromUrl = match.params.productId;

		fetchFicheProduitData();

		// Si le produit a changé, il faut réinitialiser ses choix dans le moteur de recherche sinon
		// il voit les prix d'un précédent produit
		// On regarde aussi l'id dans le cas où l'on passerait par la page /schizophrene
		// on vérifie ficheProduit.uri pour ne pas clearBooking quand on vient de la sdp listing sinon les infos
		// de la barre de recherche ne seront plus persisté
		if (
			(productUriFromUrl && ficheProduit.uri && ficheProduit.uri !== productUriFromUrl) ||
			(productIdFromUrl && ficheProduit.id && ficheProduit.id !== productIdFromUrl)
		) {
			clearBooking();
		}

		return () => {
			closeSlideshow();
			resetPrint();
		};
	}, [ficheProduit.uri, ficheProduit.id, match.params.productUri, match.params.productId]);

	useEffect(() => {
		if (isDesktop) {
			updateCurrentMerchCode(fromMerch);
		}
	}, [fromMerch, isDesktop]);

	return <FicheProduit {...props} />;
};

/* eslint react/forbid-prop-types:0 */
FicheProduitContainer.propTypes = {
	fetchFicheProduit: PropTypes.func,
	fetchFicheProduitUri: PropTypes.func,
	ficheProduit: ficheProduitPropTypes,
	toggleAsideMenu: PropTypes.func,
	slideshow: PropTypes.shape({
		isSlideshowOpened: PropTypes.bool,
		photosType: PropTypes.string,
	}),
	closeSlideshow: PropTypes.func,
	openSlideshow: PropTypes.func,
	saveNavLog: PropTypes.func,
	clearBooking: PropTypes.func,
	updateBooking: PropTypes.func,
	resetPrint: PropTypes.func,
	shouldFetchNavlogs: PropTypes.bool,
	match: PropTypes.object,
	updateCurrentMerchCode: PropTypes.func,
	resolution: PropTypes.string,
	startPrint: PropTypes.func,
	showTransportOfferOnly: PropTypes.bool,
};

FicheProduitContainer.defaultProps = {
	ficheProduit: {},
	match: {},
};

const mapStateToProps = state => {
	return {
		resolution: state.resolution,
		ficheProduit: state.ficheProduit,
		prePackageOffers: getAvailablePrepackageOffers(state),
		slideshow: state.slideshow,
		mobileSlideshowPhotos: state.ficheProduit.photos,
		desktopSlideShowPhotos: getDesktopSlideShowPhotos(state),
		videos: getVideos(state),
		isWhiteLabel: isWhiteLabel(state),
		negociated: getAllNegociated(state),
		allAvailableDurations: getAllAvailableDurations(state),
		partnerCode: state.partner.code,
		shouldFetchNavlogs: shouldFetchNavlogs(state),
		specialOffer: getSpecialOffer(state),
		shouldFetchLookAlikeProduts: state.partner.enableLookAlikeProducts,
		googleAPIKey: state.envVars.GOOGLE_API_KEY,
		showPfsMessage: getShowPfsMessage(state),
		showTransportOfferOnly: state.partner.showTransportOfferOnly,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchFicheProduit,
			fetchFicheProduitUri,
			toggleAsideMenu,
			closeSlideshow,
			openSlideshow,
			clearBooking,
			updateBooking,
			startPrint,
			resetPrint,
			saveNavLog,
			updateCurrentMerchCode,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(FicheProduitContainer);
